import React, { useState, useEffect, useCallback } from 'react';
import { ExternalLink, CreditCard, Plane, Hotel, ShoppingBag, Shield } from 'lucide-react';
import { getAllAffiliateLinks } from '../services/affiliateService';
import './AffiliateSection.css';

function AffiliateSection() {
  const [activeCategory, setActiveCategory] = useState('all');
  const [affiliateLinks, setAffiliateLinks] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadAffiliateLinks();
  }, []);  // Load once on mount

  const loadAffiliateLinks = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getAllAffiliateLinks();
      if (response.success) {
        setAffiliateLinks(response.data);
      }
    } catch (err) {
      console.error('Failed to load affiliate links:', err);
      setError('Fehler beim Laden der Partner-Links');
    } finally {
      setLoading(false);
    }
  }, []);

  const categories = [
    { id: 'all', label: 'Alle', icon: ShoppingBag },
    { id: 'flights', label: 'Flüge', icon: Plane },
    { id: 'accommodation', label: 'Unterkunft', icon: Hotel },
    { id: 'creditCards', label: 'Kreditkarten', icon: CreditCard },
    { id: 'outdoor', label: 'Outdoor', icon: ShoppingBag },
    { id: 'insurance', label: 'Versicherung', icon: Shield }
  ];

  const getFilteredLinks = () => {
    if (activeCategory === 'all') {
      return Object.values(affiliateLinks).flat();
    }
    return affiliateLinks[activeCategory] || [];
  };

  const filteredLinks = getFilteredLinks();

  if (loading) {
    return (
      <section className="section affiliate-section">
        <div className="affiliate-header">
          <h2 className="section-title">Unsere Partner & Empfehlungen</h2>
          <p className="section-subtitle">Lädt...</p>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="section affiliate-section">
        <div className="affiliate-header">
          <h2 className="section-title">Unsere Partner & Empfehlungen</h2>
          <p className="section-subtitle" style={{ color: 'var(--coral)' }}>{error}</p>
        </div>
      </section>
    );
  }

  return (
    <section className="section affiliate-section">
      <div className="affiliate-header">
        <h2 className="section-title">Unsere Partner & Empfehlungen</h2>
        <p className="section-subtitle">
          Spare Geld mit unseren geprüften Partner-Links - von Flugbuchungen bis Outdoor-Ausrüstung
        </p>
      </div>

      <div className="category-tabs">
        {categories.map(cat => {
          const Icon = cat.icon;
          return (
            <button
              key={cat.id}
              className={`tab-btn ${activeCategory === cat.id ? 'active' : ''}`}
              onClick={() => setActiveCategory(cat.id)}
            >
              <Icon size={20} />
              {cat.label}
            </button>
          );
        })}
      </div>

      <div className="affiliate-grid">
        {filteredLinks.map((link) => (
          <div key={link.id} className="affiliate-card card card-hover">
            <div className="affiliate-category-badge">{link.display_category}</div>
            <h3 className="affiliate-name">{link.name}</h3>
            <p className="affiliate-description">{link.description}</p>
            
            {link.benefits && (
              <ul className="benefits-list">
                {link.benefits.map((benefit, idx) => (
                  <li key={idx} className="benefit-item">✓ {benefit}</li>
                ))}
              </ul>
            )}
            
            <a href={link.link} className="affiliate-link" target="_blank" rel="noopener noreferrer">
              <span>Zum Angebot</span>
              <ExternalLink size={18} />
            </a>
          </div>
        ))}
      </div>

      <div className="affiliate-note">
        <p>
          🔗 <strong>Transparenz:</strong> Bei Buchungen über unsere Partner-Links erhalten wir eine kleine Provision - 
          für dich entstehen keine Mehrkosten. So können wir Just Travel weiterhin kostenlos anbieten!
        </p>
      </div>
    </section>
  );
}

export default AffiliateSection;
